/**
 * iOS In-App Purchase Service
 * Handles iOS purchases using cordova-plugin-purchase
 * Product: Silver (Mobile) monthly subscription
 */

import { Capacitor } from '@capacitor/core';
import { subscriptionService } from './index';

// Product ID for Silver (Mobile) monthly subscription
const MONTHLY_PRODUCT_ID = 'com.voxia.submobile.monthly';

class IOSPurchaseService {
  constructor() {
    this.store = null;
    this.isInitialized = false;
    this.purchaseCallback = null;
    this.errorCallback = null;
    this.isRestoring = false; // Flag to prevent approved handler during restore
  }

  /**
   * Wait for CdvPurchase to be available
   */
  async waitForCdvPurchase(maxAttempts = 20) {
    for (let i = 0; i < maxAttempts; i++) {
      if (window.CdvPurchase && window.CdvPurchase.store) {
        console.log('iOSPurchaseService: CdvPurchase is available');
        return window.CdvPurchase;
      }
      console.log(`iOSPurchaseService: Waiting for CdvPurchase... attempt ${i + 1}`);
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    throw new Error('CdvPurchase not available after waiting');
  }

  /**
   * Clear old/pending transactions
   */
  async clearPendingTransactions() {
    console.log('iOSPurchaseService: Clearing pending transactions...');
    
    try {
      const receipts = this.store.receipts || [];
      
      for (const receipt of receipts) {
        if (receipt.transactions) {
          for (const transaction of receipt.transactions) {
            const productId = transaction.products?.[0]?.id;
            
            // Finish any approved transactions that aren't our current product
            if (transaction.state === 'approved' && productId !== MONTHLY_PRODUCT_ID) {
              console.log('iOSPurchaseService: Finishing old transaction:', transaction.transactionId, 'for product:', productId);
              transaction.finish();
            }
          }
        }
      }
      
      console.log('iOSPurchaseService: ✅ Pending transactions cleared');
    } catch (error) {
      console.warn('iOSPurchaseService: Error clearing transactions:', error);
    }
  }

  /**
   * Initialize the iOS purchase store
   */
  async initialize() {
    if (this.isInitialized) {
      console.log('iOSPurchaseService: Already initialized');
      return;
    }

    // Only initialize on iOS platform
    if (Capacitor.getPlatform() !== 'ios') {
      console.warn('iOSPurchaseService: Not on iOS platform, skipping initialization');
      return;
    }

    try {
      console.log('iOSPurchaseService: Starting initialization...');

      // Wait for CdvPurchase to be available
      const CdvPurchase = await this.waitForCdvPurchase();
      
      const { store, ProductType, Platform } = CdvPurchase;
      this.store = store;

      console.log('iOSPurchaseService: CdvPurchase loaded successfully');

      // Enable verbose logging if available
      try {
        if (CdvPurchase.LogLevel) {
          store.verbosity = CdvPurchase.LogLevel.DEBUG;
          console.log('iOSPurchaseService: Verbose logging enabled');
        }
      } catch (error) {
        console.warn('iOSPurchaseService: Could not set verbosity:', error);
      }

      // Register the Silver (Mobile) monthly subscription product
      console.log('iOSPurchaseService: Registering product:', MONTHLY_PRODUCT_ID);
      
      this.store.register([
        {
          id: MONTHLY_PRODUCT_ID,
          type: ProductType.PAID_SUBSCRIPTION,
          platform: Platform.APPLE_APPSTORE,
        },
      ]);

      console.log('iOSPurchaseService: Product registered, setting up event listeners');

      // Set up event listeners for purchase lifecycle
      this.setupEventListeners();

      console.log('iOSPurchaseService: Event listeners set up, initializing store');

      // Initialize the store
      await this.store.initialize([Platform.APPLE_APPSTORE]);

      console.log('iOSPurchaseService: Store initialized');

      // Clear any old/invalid transactions
      await this.clearPendingTransactions();

      // Log registered products
      const product = this.store.get(MONTHLY_PRODUCT_ID);
      
      if (!product) {
        console.error('iOSPurchaseService: ❌ Product NOT found in App Store!');
        console.error('iOSPurchaseService: This means the product is not properly configured in App Store Connect');
        console.error('iOSPurchaseService: Please ensure:');
        console.error('iOSPurchaseService: 1. Product exists in App Store Connect');
        console.error('iOSPurchaseService: 2. Product status is "Ready to Submit" or "Approved"');
        console.error('iOSPurchaseService: 3. You are testing with TestFlight or a properly configured Sandbox account');
      } else {
        console.log('iOSPurchaseService: Product found:', JSON.stringify(product, null, 2));
      }

      this.isInitialized = true;
      console.log('iOSPurchaseService: ✅ Initialization complete');
    } catch (error) {
      console.error('iOSPurchaseService: ❌ Initialization failed');
      console.error('iOSPurchaseService: Error message:', error?.message || 'Unknown error');
      console.error('iOSPurchaseService: Error stack:', error?.stack || 'No stack trace');
      throw error;
    }
  }

  /**
   * Get receipt data from transaction or App Store
   * @param {Object} transaction - The transaction object
   * @returns {Promise<string>} Receipt data (base64 encoded)
   */
  async getReceiptData(transaction) {
    console.log('iOSPurchaseService: Getting receipt data...');

    // Method 1: Check transaction's parent receipt (most reliable for new purchases in CdvPurchase v13+)
    if (transaction?.parentReceipt?.nativeData?.appStoreReceipt) {
      console.log('iOSPurchaseService: Found receipt in transaction.parentReceipt.nativeData');
      return transaction.parentReceipt.nativeData.appStoreReceipt;
    }

    // Method 2: Check nativePurchase object on transaction
    if (transaction?.nativePurchase) {
      console.log('iOSPurchaseService: Checking nativePurchase object');

      if (transaction.nativePurchase.transactionReceipt) {
        console.log('iOSPurchaseService: Found receipt in nativePurchase.transactionReceipt');
        return transaction.nativePurchase.transactionReceipt;
      }

      if (transaction.nativePurchase.appStoreReceipt) {
        console.log('iOSPurchaseService: Found receipt in nativePurchase.appStoreReceipt');
        return transaction.nativePurchase.appStoreReceipt;
      }
    }

    // Method 3: Get from store.localReceipts (CdvPurchase v13+ way)
    console.log('iOSPurchaseService: Checking store.localReceipts...');
    const localReceipts = this.store.localReceipts || [];
    console.log('iOSPurchaseService: Found', localReceipts.length, 'local receipts');

    for (const receipt of localReceipts) {
      if (receipt?.nativeData?.appStoreReceipt) {
        console.log('iOSPurchaseService: Found receipt in localReceipts.nativeData.appStoreReceipt, length:', receipt.nativeData.appStoreReceipt.length);
        return receipt.nativeData.appStoreReceipt;
      }
    }

    // Method 4: Try store.receipts array
    console.log('iOSPurchaseService: Checking store.receipts...');
    const storeReceipts = this.store.receipts || [];
    console.log('iOSPurchaseService: Found', storeReceipts.length, 'store receipts');

    for (const receipt of storeReceipts) {
      if (receipt?.nativeData?.appStoreReceipt) {
        console.log('iOSPurchaseService: Found receipt in store.receipts.nativeData.appStoreReceipt, length:', receipt.nativeData.appStoreReceipt.length);
        return receipt.nativeData.appStoreReceipt;
      }
    }

    console.error('iOSPurchaseService: ❌ No receipt found in any source');
    console.error('iOSPurchaseService: localReceipts:', JSON.stringify(localReceipts.map(r => ({ hasNativeData: !!r?.nativeData, hasReceipt: !!r?.nativeData?.appStoreReceipt }))));
    console.error('iOSPurchaseService: storeReceipts:', JSON.stringify(storeReceipts.map(r => ({ hasNativeData: !!r?.nativeData, hasReceipt: !!r?.nativeData?.appStoreReceipt }))));

    throw new Error('Could not retrieve receipt from any source. Please try restoring purchases.');
  }

  /**
   * Set up event listeners for purchase lifecycle
   */
  setupEventListeners() {
    console.log('iOSPurchaseService: Setting up event listeners');

    // Handle approved purchases
    this.store.when().approved(async (transaction) => {
      console.log('iOSPurchaseService: ✅ Purchase APPROVED');
      console.log('iOSPurchaseService: Transaction ID:', transaction.transactionId);

      // Skip if we're in restore mode - restore flow handles its own transaction
      if (this.isRestoring) {
        console.log('iOSPurchaseService: Skipping approved handler - restore in progress');
        return;
      }

      console.log('iOSPurchaseService: Products:', transaction.products);
      console.log('iOSPurchaseService: Full transaction:', JSON.stringify(transaction, null, 2));

      // Check for product ID mismatch
      const purchasedProductId = transaction.products?.[0]?.id;
      if (purchasedProductId !== MONTHLY_PRODUCT_ID) {
        console.error('iOSPurchaseService: ❌ PRODUCT ID MISMATCH!');
        console.error(`Expected: ${MONTHLY_PRODUCT_ID}`);
        console.error(`Got: ${purchasedProductId}`);
        console.error('This is an old transaction - finishing it');

        transaction.finish();
        return;
      }

      try {
        // Get the original transaction ID for linking
        const originalTransactionId = transaction.transactionId;

        if (!originalTransactionId) {
          throw new Error('No transaction ID available');
        }

        // Extract productId
        const productId = transaction.products?.[0]?.id || MONTHLY_PRODUCT_ID;

        // Calculate expiresDate: purchaseDate + 1 month for monthly subscription
        let expiresDate = null;
        if (transaction.purchaseDate) {
          const purchaseDate = new Date(transaction.purchaseDate);
          const expDate = new Date(purchaseDate);
          expDate.setMonth(expDate.getMonth() + 1);
          expiresDate = expDate.toISOString();
        }

        // Detect environment: Sandbox transaction IDs start with 2000001
        const environment = originalTransactionId?.startsWith('2000001') ? 'Sandbox' : 'Production';

        // Build the payload to send to server
        const payload = {
          originalTransactionId,
          productId,
          expiresDate,
          environment
        };

        console.log('iOSPurchaseService: 📤 Linking transaction to user');
        console.log('iOSPurchaseService: 📤 PAYLOAD BEING SENT TO SERVER:', JSON.stringify(payload, null, 2));

        // Link the transaction to the current user with full details
        const response = await subscriptionService.linkTransaction(originalTransactionId, {
          productId,
          expiresDate,
          environment
        });
        console.log('iOSPurchaseService: 📥 Link response:', JSON.stringify(response?.data));

        // Check for success: HTTP 200/201, or success flag, or activated flag
        // Response structure: {success:true, data:{message:"...", activated:true}}
        const isSuccess = response?.status === 200 ||
                          response?.status === 201 ||
                          response?.data?.success === true ||
                          response?.data?.data?.activated === true ||
                          response?.data?.message?.includes('successfully') ||
                          response?.data?.message?.includes('activated');

        if (isSuccess) {
          console.log('iOSPurchaseService: ✅ Transaction linked successfully');

          // Finish the transaction - Apple webhook will activate the subscription
          transaction.finish();

          // Call success callback
          // Note: Subscription may not be immediately active - webhook will activate it
          if (this.purchaseCallback) {
            this.purchaseCallback({ linked: true, transactionId: originalTransactionId });
          }
        } else {
          console.error('iOSPurchaseService: ❌ Failed to link transaction');
          console.error('iOSPurchaseService: Response:', JSON.stringify(response));

          // DO NOT finish transaction - let Apple retry
          if (this.errorCallback) {
            this.errorCallback(new Error('Failed to link transaction'));
          }
        }
      } catch (error) {
        console.error('iOSPurchaseService: ❌ Error in approved handler');
        console.error('iOSPurchaseService: Error message:', error?.message || error);
        console.error('iOSPurchaseService: Error stack:', error?.stack);

        // DO NOT finish transaction on error - Apple will retry the approved event
        // The transaction stays pending until we can successfully link with backend
        // This is CRITICAL: if we finish here, Apple won't retry and user is stuck

        if (this.errorCallback) {
          this.errorCallback(error);
        }
      }
    });

    // Handle verified purchases
    this.store.when().verified((receipt) => {
      console.log('iOSPurchaseService: Receipt verified by plugin');
    });

    // Handle finished purchases
    this.store.when().finished((transaction) => {
      console.log('iOSPurchaseService: Transaction finished:', transaction.transactionId);
    });

    // Handle unverified purchases
    this.store.when().unverified((error) => {
      console.error('iOSPurchaseService: ❌ Unverified purchase:', error);
    });

    console.log('iOSPurchaseService: Event listeners configured');
  }

  /**
   * Initiate a purchase for the Silver (Mobile) monthly subscription
   * @param {Function} onSuccess - Callback when purchase succeeds
   * @param {Function} onError - Callback when purchase fails
   * @returns {Promise<void>}
   */
  async initiatePurchase(onSuccess, onError) {
    console.log('iOSPurchaseService: initiatePurchase called');

    if (!this.isInitialized) {
      console.log('iOSPurchaseService: Not initialized, initializing now...');
      try {
        await this.initialize();
      } catch (error) {
        console.error('iOSPurchaseService: Failed to initialize:', error);
        if (onError) {
          onError(error);
        }
        return;
      }
    }

    this.purchaseCallback = onSuccess;
    this.errorCallback = onError;

    try {
      // Get the product
      const product = this.store.get(MONTHLY_PRODUCT_ID);

      if (!product) {
        const errorMsg = `Product ${MONTHLY_PRODUCT_ID} not found in App Store. Please ensure it's properly configured in App Store Connect and approved for testing.`;
        console.error('iOSPurchaseService: ❌', errorMsg);
        throw new Error(errorMsg);
      }

      console.log('iOSPurchaseService: Product found');
      console.log('iOSPurchaseService: - Can purchase:', product.canPurchase);

      // Check if product can be purchased
      if (!product.canPurchase) {
        console.error('iOSPurchaseService: ❌ Product cannot be purchased');
        throw new Error('Product cannot be purchased at this time');
      }

      console.log('iOSPurchaseService: Getting offer...');
      const offer = product.getOffer();
      
      if (!offer) {
        console.error('iOSPurchaseService: ❌ No offer available');
        throw new Error('No offer available for this product');
      }

      console.log('iOSPurchaseService: Initiating order...');
      await offer.order();
      console.log('iOSPurchaseService: ✅ Order initiated');

    } catch (error) {
      console.error('iOSPurchaseService: ❌ Purchase initiation failed');
      console.error('iOSPurchaseService: Error:', error?.message || error);
      if (onError) {
        onError(error);
      }
    }
  }

  /**
   * Restore previous purchases
   */
  async restorePurchases(onSuccess, onError) {
    console.log('iOSPurchaseService: restorePurchases called');

    if (!this.isInitialized) {
      try {
        await this.initialize();
      } catch (error) {
        if (onError) onError(error);
        return;
      }
    }

    this.purchaseCallback = onSuccess;
    this.errorCallback = onError;

    try {
      // Set flag to prevent approved handler from processing during restore
      this.isRestoring = true;

      await this.store.restorePurchases();
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Find transaction for our product
      let foundTransaction = null;
      const receipts = this.store.localReceipts || this.store.receipts || [];

      for (const receipt of receipts) {
        if (receipt.transactions) {
          for (const transaction of receipt.transactions) {
            const productId = transaction.products?.[0]?.id;
            if (productId === MONTHLY_PRODUCT_ID) {
              foundTransaction = transaction;
              break;
            }
          }
        }
        if (foundTransaction) break;
      }

      if (!foundTransaction) {
        throw new Error('No previous purchases found');
      }

      // Log full transaction object for debugging
      console.log('iOSPurchaseService: Found transaction for restore:', JSON.stringify(foundTransaction, null, 2));

      // Extract transaction details for the backend
      // Try multiple possible property paths for each field
      const originalTransactionId = foundTransaction.transactionId ||
                                     foundTransaction.originalTransactionId ||
                                     foundTransaction.nativePurchase?.originalTransactionId ||
                                     foundTransaction.nativePurchase?.transactionIdentifier;

      // productId - try multiple paths
      const productId = foundTransaction.products?.[0]?.id ||
                        foundTransaction.productId ||
                        foundTransaction.nativePurchase?.productId ||
                        foundTransaction.nativePurchase?.productIdentifier ||
                        MONTHLY_PRODUCT_ID;

      // expiresDate - try multiple paths and formats
      let expiresDate = null;
      const rawExpiresDate = foundTransaction.expirationDate ||
                              foundTransaction.expiresDate ||
                              foundTransaction.nativePurchase?.expiresDate ||
                              foundTransaction.nativePurchase?.expirationDate ||
                              foundTransaction.nativePurchase?.expires_date_ms;

      if (rawExpiresDate) {
        // Handle milliseconds timestamp
        if (typeof rawExpiresDate === 'number' || /^\d+$/.test(rawExpiresDate)) {
          expiresDate = new Date(parseInt(rawExpiresDate)).toISOString();
        } else {
          expiresDate = new Date(rawExpiresDate).toISOString();
        }
      }

      // environment - try multiple paths
      const rawEnvironment = foundTransaction.environment ||
                              foundTransaction.nativePurchase?.environment ||
                              this.store.environment;

      let environment = 'Production'; // Default to Production
      if (rawEnvironment) {
        const envLower = String(rawEnvironment).toLowerCase();
        if (envLower.includes('sandbox') || envLower === 'sandbox') {
          environment = 'Sandbox';
        }
      }

      // Build the payload to send to server
      const payload = {
        originalTransactionId,
        productId,
        expiresDate,
        environment
      };

      console.log('iOSPurchaseService: Extracted transaction details (raw):', {
        rawExpiresDate,
        rawEnvironment
      });
      console.log('iOSPurchaseService: 📤 RESTORE PAYLOAD BEING SENT TO SERVER:', JSON.stringify(payload, null, 2));

      // Call linkTransaction with full details for restore
      const response = await subscriptionService.linkTransaction(originalTransactionId, {
        productId,
        expiresDate,
        environment
      });

      console.log('iOSPurchaseService: 📥 Restore link response:', JSON.stringify(response?.data));

      // Check for success: HTTP 200/201 status, or success/activated in response
      const isSuccess = response?.status === 200 ||
                        response?.status === 201 ||
                        response?.data?.activated === true ||
                        response?.data?.message?.includes('successfully') ||
                        response?.data?.message?.includes('activated');

      if (isSuccess) {
        console.log('iOSPurchaseService: ✅ Subscription restored and linked');
        this.isRestoring = false;
        if (onSuccess) onSuccess({ linked: true, transactionId: originalTransactionId });
      } else {
        throw new Error('Failed to link restored transaction');
      }
    } catch (error) {
      console.error('iOSPurchaseService: ❌ Restore failed:', error);
      this.isRestoring = false;
      if (onError) onError(error);
    }
  }

  /**
   * Manually verify the current app receipt with the backend
   * Use this for recovery when purchase succeeded but verification failed
   * @returns {Promise<Object>} The verification response
   */
  async verifyCurrentReceipt() {
    console.log('iOSPurchaseService: Manual receipt verification requested');

    if (!this.isInitialized) {
      console.log('iOSPurchaseService: Not initialized, initializing for verification...');
      await this.initialize();
    }

    try {
      // Use the same getReceiptData method that handles CdvPurchase v13+ properly
      const receiptData = await this.getReceiptData({});

      console.log('iOSPurchaseService: Receipt obtained for manual verification, length:', receiptData.length);
      console.log('iOSPurchaseService: 📤 Sending receipt to backend for manual verification...');

      const response = await subscriptionService.verifyAppleReceipt(receiptData);
      console.log('iOSPurchaseService: 📥 Manual verification response:', JSON.stringify(response));

      return response;
    } catch (error) {
      console.error('iOSPurchaseService: ❌ Manual verification failed');
      console.error('iOSPurchaseService: Error:', error?.message || error);
      throw error;
    }
  }

  /**
   * Get product information
   */
  getProduct() {
    if (!this.isInitialized || !this.store) {
      return null;
    }

    const product = this.store.get(MONTHLY_PRODUCT_ID);
    if (!product) return null;

    return {
      id: product.id,
      title: product.title,
      description: product.description,
      price: product.pricing?.price || 'N/A',
      currency: product.pricing?.currency || 'CHF',
      canPurchase: product.canPurchase,
      owned: product.owned,
    };
  }
}

// Export singleton instance
export const iOSPurchaseService = new IOSPurchaseService();
export default iOSPurchaseService;